import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AiChatResponseStatus } from '@payer-admin-ui/api/src/ai/responses/AiChatResponse';
import type { AiChatAttachmentResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatAttachmentsUploadResponse';
import type { PayerErrorLike } from '@payer-admin-ui/components-v2/src/error/PayerErrorMessage';
import type { AiChatAttachment, AiChatMessage } from './aiChatStateModel';
import { entityAdapter, getOrCreateConversation, initialState } from './aiChatStateModel';
import { appendStreamingServerMessage } from './aiChatActions';
import {
  deleteAiChatAttachment,
  fetchAiChatConversation,
  listAiChatConversations,
  retryAiChat,
  sendAiChat,
  uploadAiChatAttachments,
} from './aiChatThunks';

const aiChatSlice = createSlice({
  name: 'ai/chat',
  initialState,
  reducers: {
    hydrateOpenConversations: (state, { payload }: PayloadAction<{ openConversationIds: string[] }>) => {
      const uniqueIds = payload.openConversationIds
        .map(id => id.trim())
        .filter(id => id.length > 0)
        .filter((id, idx, arr) => arr.indexOf(id) === idx);

      uniqueIds.forEach(conversationId => {
        const conversation = getOrCreateConversation(state, conversationId);
        conversation.isEphemeral = false;
      });
      state.openConversationIds = uniqueIds;
      state.activeConversationId = uniqueIds[0] ?? null;
    },
    createConversation: (state, { payload }: PayloadAction<{ conversationId: string }>) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.isEphemeral = true;
      conversation.error = null;
      conversation.attachmentError = null;
      conversation.lastModelMode = null;
      conversation.isUploadingAttachments = false;
      conversation.deletingAttachmentIds = [];
      conversation.attachments = [];
      conversation.attachmentIds = [];
      conversation.isFetching = false;
      state.activeConversationId = payload.conversationId;

      state.openConversationIds = [
        payload.conversationId,
        ...state.openConversationIds.filter(id => id !== payload.conversationId),
      ];
    },
    openConversationTab: (state, { payload }: PayloadAction<{ conversationId: string }>) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.error = null;
      conversation.attachmentError = null;

      state.activeConversationId = payload.conversationId;
      state.openConversationIds = [
        payload.conversationId,
        ...state.openConversationIds.filter(id => id !== payload.conversationId),
      ];
    },
    addConversationAttachments: (
      state,
      { payload }: PayloadAction<{ conversationId: string; attachments: AiChatAttachmentResponse[] }>,
    ) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      const existingIds = new Set(conversation.attachmentIds);
      const nextAttachments = [...conversation.attachments];
      payload.attachments.forEach(attachment => {
        if (existingIds.has(attachment.id)) return;
        existingIds.add(attachment.id);
        nextAttachments.push({
          id: attachment.id,
          originalFilename: attachment.originalFilename ?? null,
          contentType: attachment.contentType ?? null,
          sizeBytes: attachment.sizeBytes,
          creationTime: null,
        } satisfies AiChatAttachment);
      });
      conversation.attachments = nextAttachments;
      conversation.attachmentIds = nextAttachments.map(a => a.id);
      conversation.attachmentError = null;
      conversation.isUploadingAttachments = false;
    },
    removeConversationAttachment: (
      state,
      { payload }: PayloadAction<{ conversationId: string; attachmentId: string }>,
    ) => {
      const conversation = state.entities[payload.conversationId];
      if (!conversation) return;
      conversation.attachments = conversation.attachments.filter(a => a.id !== payload.attachmentId);
      conversation.attachmentIds = conversation.attachmentIds.filter(id => id !== payload.attachmentId);
    },
    setConversationAttachmentError: (
      state,
      { payload }: PayloadAction<{ conversationId: string; error: PayerErrorLike | null }>,
    ) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.attachmentError = payload.error;
      conversation.isUploadingAttachments = false;
    },
    setConversationIsUploadingAttachments: (
      state,
      { payload }: PayloadAction<{ conversationId: string; isUploading: boolean }>,
    ) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.isUploadingAttachments = payload.isUploading;
      if (payload.isUploading) conversation.attachmentError = null;
    },
    closeConversation: (state, { payload }: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = payload;
      const wasActive = state.activeConversationId === conversationId;

      state.openConversationIds = state.openConversationIds.filter(id => id !== conversationId);

      if (!wasActive) return;

      state.activeConversationId = state.openConversationIds[0] ?? null;
    },
    resetConversationError: (state, { payload }: PayloadAction<string>) => {
      const conversation = state.entities[payload];
      if (!conversation) return;
      conversation.error = null;
      conversation.attachmentError = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(appendStreamingServerMessage, (state, { payload }) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      const role = payload.message.role;
      if (!role) return;

      conversation.messages.push({
        id: `${payload.requestId}-server-${payload.messageIndex}-${String(role).toLowerCase()}`,
        role,
        content: payload.message.content,
        createdAt: new Date().toISOString(),
        requestId: payload.requestId,
      });
    });

    builder.addCase(listAiChatConversations.fulfilled, (state, { payload }) => {
      entityAdapter.upsertMany(
        state,
        payload.map(summary => {
          const conversation = getOrCreateConversation(state, summary.conversationId);
          return {
            ...conversation,
            id: summary.conversationId,
            isEphemeral: false,
            modificationTime: summary.modificationTime,
            title: summary.title ?? conversation.title ?? null,
          };
        }),
      );
    });

    builder.addCase(fetchAiChatConversation.pending, (state, { meta }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isLoadingHistory = true;
      conversation.error = null;
    });

    builder.addCase(fetchAiChatConversation.fulfilled, (state, { payload }) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.isLoadingHistory = false;
      conversation.error = null;
      conversation.isEphemeral = false;
      conversation.creationTime = payload.creationTime;
      conversation.modificationTime = payload.modificationTime;
      conversation.title = payload.title ?? conversation.title ?? null;
      const attachments = Array.isArray(payload.attachments) ? payload.attachments : [];
      conversation.attachments = attachments.map(attachment => ({
        id: attachment.id,
        originalFilename: attachment.originalFilename ?? null,
        contentType: attachment.contentType ?? null,
        sizeBytes: attachment.sizeBytes,
        creationTime: attachment.creationTime,
      }));
      conversation.attachmentIds = conversation.attachments.map(a => a.id);
      conversation.hasLoadedMessages = true;
      conversation.lastResponseStatus = null;
      conversation.messages = payload.messages
        .map((message, idx) => {
          const role = message.role;
          if (!role) return null;
          const nextMessage: AiChatMessage = {
            id: `${payload.conversationId}-${idx}-${String(role).toLowerCase()}`,
            role,
            content: message.content,
            createdAt: payload.modificationTime,
            requestId: payload.conversationId,
          };
          return nextMessage;
        })
        .filter((message): message is AiChatMessage => message !== null);
      conversation.lastResponseStatusInfo = null;
    });

    builder.addCase(fetchAiChatConversation.rejected, (state, { meta, error }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isLoadingHistory = false;
      conversation.error = error;
    });

    builder.addCase(sendAiChat.pending, (state, { meta }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isEphemeral = false;
      conversation.isFetching = true;
      conversation.error = null;
      conversation.lastUserMessage = meta.arg.message;
      conversation.lastModelMode = meta.arg.modelMode;
      conversation.lastResponseStatus = null;
      conversation.lastResponseStatusInfo = null;
      state.activeConversationId = meta.arg.conversationId;
      state.openConversationIds = [
        meta.arg.conversationId,
        ...state.openConversationIds.filter(id => id !== meta.arg.conversationId),
      ];
      conversation.messages.push({
        id: `${meta.requestId}-user`,
        role: 'user',
        content: { type: 'text', text: meta.arg.message },
        createdAt: new Date().toISOString(),
        requestId: meta.requestId,
      });
    });

    builder.addCase(sendAiChat.fulfilled, (state, { payload }) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.isFetching = false;
      conversation.error = null;
      state.activeConversationId = payload.conversationId;
      state.openConversationIds = [
        payload.conversationId,
        ...state.openConversationIds.filter(id => id !== payload.conversationId),
      ];
      conversation.title = payload.response.title ?? conversation.title ?? null;
      conversation.lastResponseStatus = payload.response.status;
      conversation.lastResponseStatusInfo =
        payload.response.status === AiChatResponseStatus.QUOTA_EXCEEDED ? (payload.response.info ?? null) : null;

      const responseMessages = Array.isArray(payload.response.messages) ? payload.response.messages : [];
      if (responseMessages.length === 0) return;

      const nowIso = new Date().toISOString();
      const serverMessages = responseMessages
        .map((message, idx) => {
          const role = message.role;
          if (!role) return null;
          return {
            id: `${payload.requestId}-server-${idx}-${String(role).toLowerCase()}`,
            role,
            content: message.content,
            createdAt: nowIso,
            requestId: payload.requestId,
          } satisfies AiChatMessage;
        })
        .filter((message): message is AiChatMessage => message !== null);

      conversation.messages.push(...serverMessages);
    });

    builder.addCase(sendAiChat.rejected, (state, { meta, error }) => {
      console.log('aiChatState sendAiChat rejected', error);
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isFetching = false;
      conversation.error = error;
    });

    builder.addCase(retryAiChat.pending, (state, { meta }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isEphemeral = false;
      conversation.isFetching = true;
      conversation.error = null;
      conversation.lastResponseStatus = null;
      conversation.lastResponseStatusInfo = null;
    });

    builder.addCase(retryAiChat.fulfilled, (state, { payload }) => {
      const conversation = getOrCreateConversation(state, payload.conversationId);
      conversation.isFetching = false;
      conversation.error = null;
      conversation.title = payload.response.title ?? conversation.title ?? null;
      conversation.lastResponseStatus = payload.response.status;
      conversation.lastResponseStatusInfo =
        payload.response.status === AiChatResponseStatus.QUOTA_EXCEEDED ? (payload.response.info ?? null) : null;

      const responseMessages = Array.isArray(payload.response.messages) ? payload.response.messages : [];
      if (responseMessages.length === 0) return;

      const nowIso = new Date().toISOString();
      const serverMessages = responseMessages
        .map((message, idx) => {
          const role = message.role;
          if (!role) return null;
          return {
            id: `${payload.requestId}-server-${idx}-${String(role).toLowerCase()}`,
            role,
            content: message.content,
            createdAt: nowIso,
            requestId: payload.requestId,
          } satisfies AiChatMessage;
        })
        .filter((message): message is AiChatMessage => message !== null);

      conversation.messages.push(...serverMessages);
    });

    builder.addCase(retryAiChat.rejected, (state, { meta, error }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isFetching = false;
      conversation.error = error;
    });

    builder.addCase(deleteAiChatAttachment.pending, (state, { meta }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      if (conversation.deletingAttachmentIds.includes(meta.arg.attachmentId)) return;
      conversation.deletingAttachmentIds.push(meta.arg.attachmentId);
      conversation.attachmentError = null;
    });

    builder.addCase(deleteAiChatAttachment.fulfilled, (state, { payload }) => {
      const conversation = state.entities[payload.conversationId];
      if (!conversation) return;

      conversation.deletingAttachmentIds = conversation.deletingAttachmentIds.filter(id => id !== payload.attachmentId);
      conversation.attachments = conversation.attachments.filter(a => a.id !== payload.attachmentId);
      conversation.attachmentIds = conversation.attachmentIds.filter(id => id !== payload.attachmentId);
    });

    builder.addCase(deleteAiChatAttachment.rejected, (state, { meta, payload }) => {
      const conversation = state.entities[meta.arg.conversationId];
      if (!conversation) return;

      conversation.deletingAttachmentIds = conversation.deletingAttachmentIds.filter(
        id => id !== meta.arg.attachmentId,
      );
      conversation.attachmentError = payload ?? conversation.attachmentError;
    });

    builder.addCase(uploadAiChatAttachments.pending, (state, { meta }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isUploadingAttachments = true;
      conversation.attachmentError = null;
    });

    builder.addCase(uploadAiChatAttachments.fulfilled, (state, { payload }) => {
      aiChatSlice.caseReducers.addConversationAttachments(state, {
        payload: { conversationId: payload.conversationId, attachments: payload.attachments },
        type: uploadAiChatAttachments.fulfilled.type,
      });
    });

    builder.addCase(uploadAiChatAttachments.rejected, (state, { meta, payload }) => {
      const conversation = getOrCreateConversation(state, meta.arg.conversationId);
      conversation.isUploadingAttachments = false;
      conversation.attachmentError = payload ?? conversation.attachmentError;
    });
  },
});

export const {
  hydrateOpenConversations,
  createConversation,
  openConversationTab,
  closeConversation,
  resetConversationError,
  addConversationAttachments,
  removeConversationAttachment,
  setConversationAttachmentError,
  setConversationIsUploadingAttachments,
} = aiChatSlice.actions;

export const aiChatReducer = aiChatSlice.reducer;
